/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import mbpLogger from '@module-logger/mbp';
import { useQuery } from '@apollo/client';
import { useLocation, withRouter } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import loadable from '@loadable/component';
import { GRAPHQL_ENV } from '../../../gql';
import findContent from '../../../gql/queries/findContent';
import { isEmpty } from '../../../pages/Account/utils/object';
import { getBrandName } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import GraphqlSeoHeadTags from '../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import { getFeatureFlags } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getOrderId, getOrderStatus } from '../../../../state/ducks/Common/Common-Selectors';
import { getCart } from '../../../../state/ducks/Cart/Cart-Actions';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import GenericTemplateLoader from './Loader/GenericTemplateLoader';
import SubscriptionGraphqlContainerV1 from '../SubscriptionPage/SubscriptionGraphqlContainer';

const LandingPageTemplate = loadable(() => import(/* webpackChunkName: "LandingPageTemplate" */ './LandingPage/LandingPageTemplate'));

const SubscriptionGraphqlContainer = () => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const orderId = useSelector(getOrderId);
    const brandName = useSelector(getBrandName);
    const orderStatus = useSelector(getOrderStatus);
    const featureFlags = useSelector(getFeatureFlags);
    const redirectSubscriptionPage = useSelector((state) => state?.ui?.abTesting?.redirect_subscription_page_ui);

    if (!featureFlags['enable-subscription-lp-v2']) return <SubscriptionGraphqlContainerV1 />;

    const handleTrackEvent = (ec, ea, el) => {
        dispatch(trackEvent({
            eventCategory: ec || 'Subscriptions Exp',
            eventAction: ea || 'click – button',
            eventLabel: el || 'button click',
        }));
    };

    // retreiving cart data to check passport in the cart
    useEffect(() => {
        if (!orderStatus && orderId) {
            dispatch(getCart(orderId));
        }
    }, []);

    useEffect(() => {
        // track event for subscription page ab test
        if (!isEmpty(redirectSubscriptionPage) && pathname === '/subscriptions/customizable-plan') {
            handleTrackEvent('Test Impression', 'Subscriptions LP 2', redirectSubscriptionPage.type || 'variant');
        }
    }, [pathname]);

    const FETCH_CMS_DATA = findContent({
        brand: brandName,
        contentType: 'subscription_landing_page',
        GRAPHQL_ENV,
    });
    const { loading, error, data } = useQuery(FETCH_CMS_DATA);
    if (loading && !data && !error) {
        return <GenericTemplateLoader />;
    }

    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: FETCH_CMS_DATA,
            component: 'SubscriptionPage.js',
            message: 'Error loading CMS data for subscriptions exp page',
            env: GRAPHQL_ENV,
            error,
        });
        return null;
    }

    if (!data?.findContent?.content?.entries?.[0] && isEmpty(data?.findContent?.content?.entries?.[0])) return null;

    const subscriptionData = data?.findContent?.content?.entries?.[0] || {};
    const seoData = data?.findContent?.content?.entries?.[0]?.seo;
    let SEO = {};
    if (typeof seoData !== 'undefined') {
        SEO = JSON.parse(JSON.stringify(seoData));
    }
    SEO.og = [];
    SEO.og.push(
        { property: 'og:title', content: seoData?.page_title || '' },
        { property: 'og:type', content: 'website' },
        { property: 'og:description', content: seoData?.meta_description || '' },
        { property: 'og:url', content: `https://www.${brandName}.com${pathname || ''}` },
    );

    return (
        <>
            {seoData ? <GraphqlSeoHeadTags seoData={SEO} /> : null}
            <LandingPageTemplate templateData={subscriptionData} handleTrackEvent={handleTrackEvent} />
        </>
    );
};

export default withRouter(SubscriptionGraphqlContainer);
